import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { getPaymentMethodOptions, PaymentMethodEnum } from 'src/shared/models/enums/paymentMethod.enum';
import { FeePeriodFee } from '../models/feePeriod';

type FeePeriodSummaryProps = {
  fee: FeePeriodFee;
  autogiroFee?: boolean;
  swishFee?: boolean;
  amountHeader: string;
};

type SummaryRow = {
  subject: string;
  amount?: number;
  amountAutogiro?: number;
  amountSwish?: number;
};

/**
 * Visar en avgift som inte går att ändra, alltså en period som har gällt eller som ska börja
 * gälla. Samma begrepp som avgiftsformuläret men utan fält, eftersom ingenting ska kunna
 * redigeras av misstag i en historikvy.
 */
export const FeePeriodSummary = (props: FeePeriodSummaryProps) => {
  const { fee, autogiroFee, swishFee, amountHeader } = props;
  const { t } = useTranslation();
  const { currentUser } = useGlobals();

  // Kunden kan döpa om vänmedlem i modulinställningarna. Utan eget namn heter det Vänmedlem.
  const friendLabel = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.NewFriendLabel, t('association:feePreviewFriend'));

  // Beloppen betyder olika saker beroende på metod, så perioden säger vilken den räknades med.
  const methodLabel = getPaymentMethodOptions(t).find((opt) => opt.value === fee.paymentMethod)?.description ?? '';

  const familyStepLabel = (member: string | undefined, index: number) => {
    if (member === 'mainMember') return t('association:paymentMethodFieldLabels.familyStepMainMember');
    return t('association:paymentMethodFieldLabels.familyStepfamilyMember', { count: index });
  };

  const methodRows = (): SummaryRow[] => {
    switch (fee.paymentMethod) {
      case PaymentMethodEnum.SameFeeForAll:
        return [
          {
            subject: t('association:feeTableMember'),
            amount: fee.sameFeeForAll,
            amountAutogiro: fee.sameFeeForAllAutogiro,
            amountSwish: fee.sameFeeForAllSwish,
          },
        ];

      case PaymentMethodEnum.MainAndFamily:
        return [
          {
            subject: t('association:feePreviewMainMember'),
            amount: fee.mainAndFamily?.feeMainMember,
            amountAutogiro: fee.mainAndFamily?.feeMainMemberAutogiro,
            amountSwish: fee.mainAndFamily?.feeMainMemberSwish,
          },
          {
            subject: t('association:feePreviewFamilyMember'),
            amount: fee.mainAndFamily?.feeFamilyMember,
            amountAutogiro: fee.mainAndFamily?.feeFamilyMemberAutogiro,
            amountSwish: fee.mainAndFamily?.feeFamilyMemberSwish,
          },
        ];

      case PaymentMethodEnum.FamilySteps:
        return (fee.familySteps ?? []).map((step, index) => ({
          subject: familyStepLabel(step.member, index),
          amount: step.fee,
          amountAutogiro: step.feeAutogiro,
          amountSwish: step.feeSwish,
        }));

      case PaymentMethodEnum.OneFeeForHousehold:
        return [
          {
            subject: t('association:paymentMethodFieldLabels.oneFeeForHousehold'),
            amount: fee.oneFeeForHousehold,
            amountAutogiro: fee.oneFeeForHouseholdAutogiro,
            amountSwish: fee.oneFeeForHouseholdSwish,
          },
        ];

      default:
        return [];
    }
  };

  const rows = methodRows();

  if (fee.feeFriend !== undefined && fee.feeFriend !== null) {
    rows.push({ subject: friendLabel, amount: fee.feeFriend, amountAutogiro: fee.feeFriendAutogiro, amountSwish: fee.feeFriendSwish });
  }

  const profiles = fee.feeBasedOnProfile ?? [];

  if (rows.length === 0 && profiles.length === 0) {
    return <Typography variant='body2'>{t('association:feePeriodNoAmounts')}</Typography>;
  }

  return (
    <Box>
      <Typography variant='body2' sx={{ mb: 2, color: 'text.secondary' }}>
        {t('association:feePeriodMethod', { method: methodLabel })}
      </Typography>

      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '50%' }}>{t('association:feeTableSubject')}</TableCell>
            <TableCell sx={{ width: '25%' }}>{amountHeader}</TableCell>
            {autogiroFee && <TableCell sx={{ width: '25%' }}>{t('association:autogiroFeeLabel')}</TableCell>}
            {swishFee && <TableCell sx={{ width: '25%' }}>{t('association:swishFeeLabel')}</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.subject}>
              <TableCell>{row.subject}</TableCell>
              <TableCell>{row.amount ?? ''}</TableCell>
              {autogiroFee && <TableCell>{row.amountAutogiro ?? ''}</TableCell>}
              {swishFee && <TableCell>{row.amountSwish ?? ''}</TableCell>}
            </TableRow>
          ))}

          {profiles.length > 0 && (
            <TableRow>
              <TableCell colSpan={2 + (autogiroFee ? 1 : 0) + (swishFee ? 1 : 0)} sx={{ pt: 3, border: 0 }}>
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  {t('association:feeTableProfiles')}
                </Typography>
              </TableCell>
            </TableRow>
          )}

          {profiles.map((profile) => (
            <TableRow key={`${profile.memberProfileItem_id}-${profile.fee}`}>
              <TableCell>
                {profile.profileGroup} {profile.profile}
              </TableCell>
              <TableCell>{profile.fee ?? ''}</TableCell>
              {autogiroFee && <TableCell>{profile.feeAutogiro ?? ''}</TableCell>}
              {swishFee && <TableCell>{profile.feeSwish ?? ''}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
